import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Home,
  Percent,
  Building,
  RefreshCw,
  PoundSterling,
  Building2,
  Moon,
  Brain,
  Calendar,
  Phone,
  CheckCircle
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function RemortgageGuide() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#020617] via-[#0f172a] to-[#020617] text-white">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 py-8">
        <Link to="/bio/zahirul" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Profile
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <RefreshCw className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Remortgage & Property Strategy</h1>
          <p className="text-slate-400">Thames Mortgage & Protection</p>
          <p className="text-lg text-blue-400 mt-2">Your mortgage should work for you — not against you.</p>
        </motion.div>

        {/* Secure a Better Rate */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
              <Percent className="w-5 h-5 text-green-400" />
            </div>
            <h2 className="text-xl font-semibold">📉 Secure a Better Rate</h2>
          </div>
          <ul className="space-y-2 text-slate-300">
            <li>• Product ending soon?</li>
            <li>• Moving onto lender SVR?</li>
            <li>• Looking to reduce monthly payments?</li>
          </ul>
          <p className="text-blue-400 mt-4 font-medium">We review the whole market — not just your current lender.</p>
        </motion.div>

        {/* Repay Help to Buy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Home className="w-5 h-5 text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold">🏠 Repay Help to Buy</h2>
          </div>
          <ul className="space-y-2 text-slate-300">
            <li>• Clear your equity loan</li>
            <li>• Avoid rising interest charges</li>
            <li>• Refinance into standard mortgage</li>
          </ul>
          <p className="text-blue-400 mt-4 font-medium">We guide you through valuation & lender criteria.</p>
        </motion.div>

        {/* Shared Ownership */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Building className="w-5 h-5 text-purple-400" />
            </div>
            <h2 className="text-xl font-semibold">🏡 Shared Ownership → Full Ownership</h2>
          </div>
          <ul className="space-y-2 text-slate-300">
            <li>• Staircasing support</li>
            <li>• Increase ownership percentage</li>
            <li>• Move to full mortgage structure</li>
          </ul>
          <p className="text-blue-400 mt-4 font-medium">Structured correctly to protect affordability.</p>
        </motion.div>

        {/* Let-to-Buy Strategy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-amber-400" />
            </div>
            <h2 className="text-xl font-semibold">🔄 Let-to-Buy Strategy</h2>
          </div>
          <ul className="space-y-2 text-slate-300">
            <li>• Keep current home as rental</li>
            <li>• Purchase larger family property</li>
            <li>• Structure deposit release efficiently</li>
          </ul>
          <p className="text-blue-400 mt-4 font-medium">Complex cases handled smoothly.</p>
        </motion.div>

        {/* Equity Release */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <PoundSterling className="w-5 h-5 text-emerald-400" />
            </div>
            <h2 className="text-xl font-semibold">💷 Equity Release / Capital Raise</h2>
          </div>
          <ul className="space-y-2 text-slate-300">
            <li>• Release funds for home improvement</li>
            <li>• Support family deposit</li>
            <li>• Debt consolidation</li>
            <li>• Business or investment use</li>
          </ul>
          <p className="text-blue-400 mt-4 font-medium">Always assessed responsibly and suitability-focused.</p>
        </motion.div>

        {/* Limited Company BTL */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-cyan-400" />
            </div>
            <h2 className="text-xl font-semibold">🏢 Limited Company Buy-to-Let</h2>
          </div>
          <ul className="space-y-2 text-slate-300">
            <li>• Tax-efficient property investment</li>
            <li>• SPV structure guidance</li>
            <li>• Director & shareholder advice</li>
            <li>• Portfolio landlord options</li>
          </ul>
          <p className="text-blue-400 mt-4 font-medium">Ideal for higher-rate taxpayers & long-term investors.</p>
        </motion.div>

        {/* Islamic Mortgages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 backdrop-blur border border-emerald-500/30 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Moon className="w-5 h-5 text-emerald-400" />
            </div>
            <h2 className="text-xl font-semibold">🕌 Islamic / Sharia-Compliant Mortgages Available</h2>
          </div>
          <ul className="space-y-2 text-slate-300">
            <li>• Home Purchase Plans</li>
            <li>• Ijara & Diminishing Musharaka structures</li>
            <li>• No interest-based lending</li>
            <li>• Ethical & compliant solutions</li>
          </ul>
          <p className="text-emerald-400 mt-4 font-medium">Specialist lenders available subject to criteria.</p>
        </motion.div>

        {/* Not Sure */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-rose-500/20 rounded-xl flex items-center justify-center">
              <Brain className="w-5 h-5 text-rose-400" />
            </div>
            <h2 className="text-xl font-semibold">🧠 Not Sure Which Route Is Right?</h2>
          </div>
          <p className="text-slate-300 mb-4">Every restructure affects:</p>
          <ul className="space-y-2 text-slate-300">
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Monthly payments</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Long-term interest cost</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Tax position</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Exit flexibility</li>
          </ul>
          <p className="text-blue-400 mt-4 font-medium">We analyse your full position before recommending a strategy.</p>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur border border-blue-500/30 rounded-2xl p-6 mb-6 text-center"
        >
          <h2 className="text-2xl font-bold mb-2">Book Your Mortgage Review</h2>
          <p className="text-slate-300 mb-4">Strategic advice. Structured solutions.<br/>Clear direction before you commit.</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a href="tel:07550199399" className="block">
              <Button className="w-full h-14 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-lg">
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
            </a>
            <Link to="/book-appointment" className="block">
              <Button className="w-full h-14 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg">
                <Calendar className="w-5 h-5 mr-2" />
                Schedule Your Private Review
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition text-sm">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-amber-500 rounded-lg flex items-center justify-center">
              <Home className="w-3 h-3 text-white" />
            </div>
            Thames Mortgage & Protection
          </Link>
          <p className="text-xs text-slate-500 mt-3">FCA Regulated • FRN 1045227</p>
          <p className="text-xs text-amber-500 mt-2 font-medium">Your home may be repossessed if you do not keep up repayments on your mortgage or loans secured on it.</p>
        </motion.div>
      </div>
    </div>
  );
}
